import {
  Autocomplete,
  Button,
  Divider,
  Flex,
  Grid,
  Label,
  Radio,
  RadioGroupField,
  ScrollView,
  SelectField,
  TextAreaField,
  TextField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation } from "aws-amplify";
import { useContext, useEffect, useState } from "react";
import LocalizedStrings from "react-localization";
import toast from "react-hot-toast";
import { GoogleMap, useJsApiLoader, Marker } from "@react-google-maps/api";
import tzlookup from "tz-lookup";

import useErrors from "hooks/useErrors";
import { globalStrings } from "config/translations/es";
import {
  listBranches,
  listCompanies,
  deviceVariablesByDeviceID,
  deviceActionsByDeviceID,
} from "graphql/queries";
import DeviceVariableForm from "./DeviceVariableForm";
import DeviceActionForm from "./DeviceActionForm";
import AlertsChoose from "components/alerts/AlertsChoose";
import { LayoutContext } from "layout/context/LayoutContext";

const containerStyleMap = {
  width: "100%",
  height: "350px",
};

const centerMap = {
  lat: 6.25184,
  lng: -75.56359,
};

const DeviceForm = ({ onSubmit, type = "create", initialValues }) => {
  const { me } = useContext(LayoutContext);
  const { isLoaded: mapIsLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });
  const [deviceForm, setDeviceForm] = useState(initialValues);
  const [deviceVariables, setDeviceVariables] = useState([]);
  const [deviceVariablesChanged, setDeviceVariablesChanged] = useState([]);
  const [deviceActions, setDeviceActions] = useState([]);
  const [deviceActionsChanged, setDeviceActionsChanged] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [errors, setErrors] = useState({
    companyID: "",
    branchID: "",
    name: "",
    location: "",
    brand: "",
    description: "",
    autoRefreshTime: "",
  });
  const validateErrors = useErrors({
    companyID: "isRequired",
    branchID: "isRequired",
    name: "isRequired",
    autoRefreshTime: ["isRequired", "isNumber"],
  });

  const onSubmitForm = async () => {
    let hasErrors = false;

    Object.entries(deviceForm).forEach((itemForm) => {
      const [name, value] = itemForm;
      const check = !!checkErrors(name, value);
      if (!hasErrors) hasErrors = check;
    });

    if (
      !companies.some((companyRec) => companyRec.id === deviceForm.companyID)
    ) {
      setErrors((prevSt) => ({
        ...prevSt,
        companyID: strings.validations.incorrectValue,
      }));
      return;
    }

    if (!branches.some((branchRec) => branchRec.id === deviceForm.branchID)) {
      setErrors((prevSt) => ({
        ...prevSt,
        branchID: strings.validations.incorrectValue,
      }));
      return;
    }

    if (!hasErrors) {
      let responseTimeZone = null;
      if (
        initialValues.coordsLat !== deviceForm.coordsLat &&
        initialValues.coordsLng !== deviceForm.coordsLng
      )
        responseTimeZone = tzlookup(deviceForm.coordsLat, deviceForm.coordsLng);

      onSubmit({
        ...deviceForm,
        variables: deviceVariablesChanged,
        oldVariables: deviceVariables,
        actions: deviceActionsChanged,
        oldActions: deviceActions,
        timezone: responseTimeZone ?? "America/Bogota",
      });
    }
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setDeviceForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);

    if (name === "autoRefreshTime" && value < 1)
      setErrors((prevErr) => ({
        ...prevErr,
        autoRefreshTime: strings.validations.incorrectValue,
      }));
  };

  const onChangeVariables = (values) => {
    setDeviceVariablesChanged(values);
  };

  const onChangeActions = (values) => {
    setDeviceActionsChanged(values);
  };

  const onChangeAlerts = (alerts) => {
    setDeviceForm((prevSt) => ({
      ...prevSt,
      alerts,
    }));
  };

  const onChangeSelect = (e) => {
    const { name, value } = e.target;
    if (name === "companyID") {
      fetchCompanies({ name: value });
      onClearSelect("branchID");
    }
    if (name === "branchID")
      fetchBranches({ name: value }, deviceForm.companyID);
    setDeviceForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
  };

  const onSelect = (name, value) => {
    setDeviceForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
    checkErrors(name, value);
    if (name === "companyID") {
      onClearSelect("branchID", value);
    }
  };

  const onClearSelect = (name, parentId) => {
    const value = "";
    if (name === "companyID") {
      fetchCompanies({ name: value });
      onClearSelect("branchID");
    }
    if (name === "branchID") {
      if (!parentId) setBranches([]);
      else fetchBranches({ name: value }, parentId);
    }
    setDeviceForm((prevSt) => ({
      ...prevSt,
      [name]: value,
    }));
  };

  const checkErrors = (name, value) => {
    const errorMessage = validateErrors(name, value);
    if (errorMessage)
      setErrors((prevErr) => ({ ...prevErr, [name]: errorMessage }));
    else setErrors((prevErr) => ({ ...prevErr, [name]: "" }));
    return errorMessage;
  };

  const resetFormValues = () => {
    setErrors({
      companyID: "",
      branchID: "",
      name: "",
      location: "",
      brand: "",
      description: "",
      autoRefreshTime: "",
    });
    setDeviceForm(initialValues);
    fetchCompanies({ id: initialValues.companyID });
    fetchBranches({ id: initialValues.branchID }, initialValues.companyID);
    setDeviceVariablesChanged(deviceVariables);
    setDeviceActionsChanged(deviceActions);
    setIsRefresh((prevSt) => !prevSt);
  };

  const onChangeMapMarker = (event) => {
    const { latLng } = event;
    setDeviceForm((prevSt) => ({
      ...prevSt,
      coordsLat: latLng.lat(),
      coordsLng: latLng.lng(),
    }));
  };

  async function fetchCompanies({ id = "", name = "" }) {
    let filterObj = {};
    try {
      setCompaniesLoading(true);

      if (me.companyID)
        filterObj = { ...filterObj, or: [{ id: { eq: me.companyID } }] };
      if (me.managedCompanies.length > 0)
        filterObj = {
          ...filterObj,
          or: [
            ...(filterObj?.or ?? []),
            ...me.managedCompanies.map((cp) => ({ id: { eq: cp } })),
          ],
        };

      const companiesData = await API.graphql(
        graphqlOperation(listCompanies, {
          limit:
            !id && !name && Object.keys(filterObj).length <= 0 ? 20 : undefined,
          filter: {
            and: [
              {
                id: { contains: id },
              },
              {
                name: { contains: name },
              },
            ],
            ...filterObj,
          },
        })
      );
      const companiesItems = companiesData.data.listCompanies.items;
      setCompanies(companiesItems);
      setCompaniesLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetchCompanies);
    }
  }

  async function fetchBranches({ id = "", name = "" }, companyId) {
    try {
      setBranchesLoading(true);
      const branchesData = await API.graphql(
        graphqlOperation(listBranches, {
          limit: !id && !name ? 20 : undefined,
          filter: {
            companyID: { eq: companyId },
            and: [
              {
                id: { contains: id },
              },
              {
                name: { contains: name },
              },
            ],
          },
        })
      );
      const branchesItems = branchesData.data.listBranches.items;
      setBranches(branchesItems);
      setBranchesLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetchBranches);
    }
  }

  async function fetchDeviceVariables(deviceID) {
    const deviceVariablesData = await API.graphql(
      graphqlOperation(deviceVariablesByDeviceID, {
        deviceID,
      })
    );
    const deviceVariablesItems =
      deviceVariablesData.data.deviceVariablesByDeviceID.items;
    setDeviceVariables(deviceVariablesItems);
    setDeviceVariablesChanged(deviceVariablesItems);
  }

  async function fetchDeviceActions(deviceID) {
    const deviceActionsData = await API.graphql(
      graphqlOperation(deviceActionsByDeviceID, {
        deviceID,
      })
    );
    const deviceActionsItems =
      deviceActionsData.data.deviceActionsByDeviceID.items;
    setDeviceActions(deviceActionsItems);
    setDeviceActionsChanged(deviceActionsItems);
  }

  useEffect(() => {
    setDeviceForm(initialValues);
    fetchCompanies({ id: initialValues.companyID });
    fetchBranches({ id: initialValues.branchID }, initialValues.companyID);
    if (!!initialValues?.id) {
      fetchDeviceVariables(initialValues.id);
      fetchDeviceActions(initialValues.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return (
    <>
      <Grid templateColumns={{ base: "1fr", large: "2fr 3fr" }}>
        <Grid padding="20px" rowGap="5px" alignSelf="start">
          <div className="fs-5">{strings.fields.location.label}</div>
          {mapIsLoaded && (
            <GoogleMap
              mapContainerStyle={containerStyleMap}
              center={{
                lat: deviceForm.coordsLat ?? centerMap.lat,
                lng: deviceForm.coordsLng ?? centerMap.lng,
              }}
              zoom={15}
              // onLoad={onLoad}
              // onUnmount={onUnmount}
            >
              <Marker
                draggable
                onDragEnd={onChangeMapMarker}
                position={{
                  lat: deviceForm.coordsLat ?? centerMap.lat,
                  lng: deviceForm.coordsLng ?? centerMap.lng,
                }}
              ></Marker>
            </GoogleMap>
          )}
        </Grid>
        <ScrollView maxHeight="75vh">
          <Grid padding="20px" rowGap="15px">
            <Grid
              templateColumns={{ base: "1fr", large: "2fr 2fr" }}
              rowGap="15px"
              columnGap="15px"
            >
              <TextField
                name="name"
                label={
                  <span>
                    {strings.fields.name.label}
                    <span style={{ fontSize: "0.8rem", color: "red" }}>
                      {" *"}
                    </span>
                  </span>
                }
                value={deviceForm.name}
                onChange={onChange}
                errorMessage={errors.name}
                hasError={!!errors.name}
              ></TextField>
              <TextField
                name="brand"
                label={strings.fields.brand.label}
                value={deviceForm.brand}
                onChange={onChange}
                errorMessage={errors.brand}
                hasError={!!errors.brand}
              ></TextField>
            </Grid>
            <Grid
              templateColumns={{ base: "1fr", large: "2fr 2fr" }}
              rowGap="15px"
              columnGap="15px"
            >
              {type === "create" && (
                <Flex direction="column" gap="xs">
                  <Label htmlFor="companyID">
                    <span>
                      {strings.fields.company.label}
                      <span style={{ fontSize: "0.8rem", color: "red" }}>
                        {" *"}
                      </span>
                    </span>
                  </Label>
                  <Autocomplete
                    id="companyID"
                    name="companyID"
                    label={strings.fields.company.label}
                    isRequired
                    placeholder={strings.fields.company.placeholder}
                    value={
                      companies.find(
                        (companyRec) => companyRec.id === deviceForm.companyID
                      )?.name ?? deviceForm.companyID
                    }
                    options={companies.map((companyRec) => ({
                      id: companyRec.id,
                      label: companyRec.name,
                    }))}
                    isLoading={companiesLoading}
                    onClear={() => onClearSelect("companyID")}
                    onChange={onChangeSelect}
                    onSelect={({ id }) => onSelect("companyID", id)}
                    errorMessage={errors.companyID}
                    hasError={!!errors.companyID}
                    menuSlots={{ Empty: globalStrings.noResults }}
                  ></Autocomplete>
                </Flex>
              )}
              <Flex direction="column" gap="xs">
                <Label htmlFor="branchID">
                  <span>
                    {strings.fields.branch.label}
                    <span style={{ fontSize: "0.8rem", color: "red" }}>
                      {" *"}
                    </span>
                  </span>
                </Label>
                <Autocomplete
                  id="branchID"
                  name="branchID"
                  label={strings.fields.branch.label}
                  isRequired
                  disabled={!deviceForm.companyID}
                  placeholder={strings.fields.branch.placeholder}
                  value={
                    branches.find(
                      (branchRec) => branchRec.id === deviceForm.branchID
                    )?.name ?? deviceForm.branchID
                  }
                  options={branches.map((branchRec) => ({
                    id: branchRec.id,
                    label: branchRec.name,
                  }))}
                  isLoading={branchesLoading}
                  onClear={() =>
                    onClearSelect("branchID", deviceForm.companyID)
                  }
                  onChange={onChangeSelect}
                  onSelect={({ id }) => onSelect("branchID", id)}
                  errorMessage={errors.branchID}
                  hasError={!!errors.branchID}
                  menuSlots={{ Empty: globalStrings.noResults }}
                ></Autocomplete>
              </Flex>
            </Grid>
            <Grid>
              <TextAreaField
                name="description"
                label={strings.fields.description.label}
                value={deviceForm.description}
                onChange={onChange}
                errorMessage={errors.description}
                hasError={!!errors.description}
              ></TextAreaField>
            </Grid>
            <Grid
              templateColumns={{ base: "1fr", large: "2fr 2fr" }}
              rowGap="15px"
              columnGap="15px"
            >
              <TextField
                name="energyCost"
                label={strings.fields.energyCost.label}
                value={deviceForm.energyCost ?? ""}
                type="number"
                onChange={onChange}
                errorMessage={errors.energyCost}
                hasError={!!errors.energyCost}
              ></TextField>
              <SelectField
                name="currency"
                label={strings.fields.currency.label}
                onChange={onChange}
                value={deviceForm.currency ?? ""}
              >
                <option value="">{strings.selectOption}</option>
                {Object.entries(globalStrings.currency).map((opt) => (
                  <option value={opt[0]}>{opt[1]}</option>
                ))}
              </SelectField>
              <TextField
                name="autoRefreshTime"
                label={
                  <span>
                    {strings.fields.autoRefreshTime.label}
                    <span style={{ fontSize: "0.8rem", color: "red" }}>
                      {" *"}
                    </span>
                  </span>
                }
                descriptiveText={strings.fields.autoRefreshTime.description}
                value={deviceForm.autoRefreshTime ?? ""}
                type="number"
                onChange={onChange}
                errorMessage={errors.autoRefreshTime}
                hasError={!!errors.autoRefreshTime}
              ></TextField>
            </Grid>
            <Divider className="mt-3 mb-2" />
            <DeviceVariableForm
              initialValues={deviceVariables}
              onChangeVariables={onChangeVariables}
              refreshInitialValues={isRefresh}
            />
            <Divider className="mt-3 mb-2" />
            <DeviceActionForm
              initialValues={deviceActions}
              onChangeActions={onChangeActions}
              refreshInitialValues={isRefresh}
            />
            <Divider className="mt-3 mb-2" />
            <AlertsChoose
              initAlertsGranted={initialValues.alerts ?? []}
              onChangeAlertsGranted={onChangeAlerts}
              resetValues={isRefresh}
            />
            <Divider className="mt-3 mb-2" />
            <Grid>
              <Label>{strings.fields.env.label}</Label>
              <RadioGroupField
                name="env"
                value={deviceForm.env ?? "dev"}
                onChange={onChange}
                direction="row"
              >
                {Object.entries(strings.fields.env.options).map((opt) => (
                  <Radio value={opt[0]}>{opt[1]}</Radio>
                ))}
              </RadioGroupField>
            </Grid>
            <Flex justifyContent="end">
              <Button
                children={strings.fields.buttons.reset}
                onClick={resetFormValues}
              ></Button>
              <Button
                children={
                  type === "create"
                    ? strings.fields.buttons.create
                    : strings.fields.buttons.save
                }
                onClick={onSubmitForm}
                variation="primary"
                isDisabled={Object.values(errors).some((e) => !!e)}
              ></Button>
            </Flex>
          </Grid>
        </ScrollView>
      </Grid>
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    selectOption: "Seleccione una opción",
    fields: {
      name: {
        label: "Nombre",
      },
      location: {
        label: "Ubicación",
      },
      brand: {
        label: "Marca",
      },
      description: {
        label: "Descripción",
      },
      autoRefreshTime: {
        label: "Tiempo de refresco automático",
        description: "Ingresar tiempo en segundos",
      },
      energyCost: {
        label: "Costo de energía",
      },
      currency: {
        label: "Moneda",
      },
      company: {
        label: "Empresa",
        placeholder: "Buscar empresa...",
      },
      branch: {
        label: "Sede",
        placeholder: "Buscar sede...",
      },
      env: {
        label: "Ambiente",
        options: {
          dev: "Pruebas",
          prod: "Producción",
        },
      },
      buttons: {
        create: "Crear",
        save: "Guardar",
        reset: "Restablecer",
      },
    },
    validations: {
      incorrectValue: "Valor incorrecto",
    },
    responses: {
      errorFetchCompanies: "Error al consultar empresas",
      errorFetchBranches: "Error al consultar sedes",
    },
  },
});

export default DeviceForm;
